import { PaginationResponseDto } from '@/api';
import clsx from 'clsx';
import isNil from 'lodash.isnil';
import React, { useEffect, useMemo, useState } from 'react';
import Select, { DropdownIndicatorProps } from 'react-select';
import Icon from '@/components/design-system/Icon';
import { useTranslation } from 'react-i18next';

type IPaginationProps = {
    transparent?: boolean
    data: PaginationResponseDto | undefined
    offsetChanged: (offset: number) => void;
    itemsPerPageChanged: (itemsPerPage: number) => void;
    defaultItemsPerPage: number;
    factor?: 4 | 10 | 5
};

/**
 * The DS chevron in place of react-select's own caret — bare, no tail, which
 * is what the set (35:6353) uses for disclosure. It points up while the menu
 * is open. Hoisted rather than declared inline: react-select would see a new
 * component type on every render and rebuild the menu.
 */
type ItemsPerPageOption = { id: number; value: number; label: string };

const DropdownIndicator = ({ selectProps }: DropdownIndicatorProps<ItemsPerPageOption, false>) => (
    <span className="flex items-center px-[4px] text-ink-400">
        <Icon name={selectProps.menuIsOpen ? 'chevron-up' : 'chevron-down'} size={20} />
    </span>
);

const Pagination = (props: IPaginationProps) => {
    const { data, offsetChanged: pageChanged, itemsPerPageChanged, defaultItemsPerPage, factor = 10 } = props;
    const { i18n: i18next } = useTranslation();
    const [itemsPerPageOptions, setItemsPerPageOptions] = useState(() => {
        switch (factor) {
            case 4:
                return [
                    { id: 8, value: 8, label: i18next.t('pagination.items-per-page', { count: 8 }) as string },
                    { id: 16, value: 16, label: i18next.t('pagination.items-per-page', { count: 16 }) },
                    { id: 24, value: 24, label: i18next.t('pagination.items-per-page', { count: 24 }) },
                    { id: 48, value: 48, label: i18next.t('pagination.items-per-page', { count: 48 }) },
                    { id: 96, value: 96, label: i18next.t('pagination.items-per-page', { count: 96 }) }
                ];
            case 5:
                return [
                    { id: 5, value: 5, label: i18next.t('pagination.items-per-page', { count: 5 }) as string },
                    { id: 10, value: 10, label: i18next.t('pagination.items-per-page', { count: 10 }) },
                    { id: 25, value: 25, label: i18next.t('pagination.items-per-page', { count: 25 }) },
                    { id: 50, value: 50, label: i18next.t('pagination.items-per-page', { count: 50 }) },
                    { id: 75, value: 75, label: i18next.t('pagination.items-per-page', { count: 75 }) }
                ];
            case 10:
            default:
                return [
                    { id: 5, value: 5, label: i18next.t('pagination.items-per-page', { count: 5 }) as string },
                    { id: 10, value: 10, label: i18next.t('pagination.items-per-page', { count: 10 }) },
                    { id: 20, value: 20, label: i18next.t('pagination.items-per-page', { count: 20 }) },
                    { id: 40, value: 40, label: i18next.t('pagination.items-per-page', { count: 40 }) },
                    { id: 80, value: 80, label: i18next.t('pagination.items-per-page', { count: 80 }) }
                ];
        }
    });

    const defaultItemsPerPageCount = defaultItemsPerPage || (factor === 4 ? 16 : factor === 5 ? 25 : 20);

    useEffect(() => {
        if (defaultItemsPerPage && defaultItemsPerPageCount) {
            const arrOfOptions = itemsPerPageOptions.filter((option) => {
                if (defaultItemsPerPage && defaultItemsPerPageCount === option.id) {
                    return false;
                }
                return true;
            });
            arrOfOptions.unshift({ id: defaultItemsPerPageCount, value: defaultItemsPerPageCount, label: `${defaultItemsPerPageCount}` });
            setItemsPerPageOptions(arrOfOptions.sort((a, b) => a.value - b.value));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultItemsPerPage, defaultItemsPerPageCount]);

    const [itemsPerPage, setItemsPerPage] = useState<{ id: number; value: number; label: string }>({ id: defaultItemsPerPageCount, value: defaultItemsPerPageCount, label: `${defaultItemsPerPageCount}` });

    useEffect(() => {
        setItemsPerPage({ id: defaultItemsPerPage, value: defaultItemsPerPage, label: `${defaultItemsPerPage}` });
    }, [defaultItemsPerPage]);

    const startItem = data ? (data.offset) + 1 : undefined;
    const endItem = data ? Math.min((startItem || 0) + (data.limit) - 1, data.total) : undefined;
    const totalPages = data ? Math.ceil((data.total) / (data.limit)) : undefined;
    const currentPage = data ? Math.ceil(((data.offset) + (data.limit)) / (data.limit)) : undefined;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const pagesToShow = useMemo(() => {
        if (isNil(currentPage) || isNil(totalPages)) {
            return [];
        }
        const priorityPages = [1, currentPage - 1, currentPage, currentPage + 1, totalPages];
        const totalPossiblePages = [1, currentPage - 4, currentPage - 3, currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2, currentPage + 3, currentPage + 4, totalPages];

        // eslint-disable-next-line @typescript-eslint/no-shadow
        const pagesToShow = totalPossiblePages.filter(
            (page, index, self) => page > 0 && page <= totalPages && self.indexOf(page) === index
        );
        pagesToShow.sort((a, b) => a - b);
        while (pagesToShow.length > 5) {
            if (currentPage < 3) {
                const firstNonPriroty = pagesToShow.findLastIndex((p) => !priorityPages.includes(p));
                pagesToShow.splice(firstNonPriroty, 1);
            } else {
                const firstNonPriroty = pagesToShow.findIndex((p) => !priorityPages.includes(p));
                pagesToShow.splice(firstNonPriroty, 1);
            }
        }

        if (pagesToShow.length === 5) {
            pagesToShow.splice(pagesToShow.length - 1, 0, -1);
        }

        return pagesToShow;
    }, [currentPage, totalPages]);

    if (isNil(currentPage) || isNil(totalPages) || isNil(startItem) || isNil(endItem) || isNil(data) || data.total <= 1) {
        return null;
    }

    if (pagesToShow.length <= 1 && data.total <= itemsPerPageOptions[0].value) {
        return null;
    }

    const itemsCounter = <div>{data.total} {i18next.t('common:pagination.items')}</div>;
    const itemsPerPageSelctor = (
        <div className="flex flex-row items-center">
            <span className="mx-[12px]">{i18next.t('common:pagination.display')}</span>
            <Select<ItemsPerPageOption, false>
                components={{ DropdownIndicator }}
                className="cursor-pointer"
                /*
                 * react-select is styled through JS, so it cannot use Tailwind
                 * classes — but it can read the DS variables directly, which
                 * keeps it on the tokens instead of the hardcoded #cccccc,
                 * #001f308f and #2D5271 it used to carry.
                 *
                 * `font: inherit` everywhere pulls the family, size and weight
                 * from the wrapper's text-body-400 rather than react-select's
                 * own defaults. 33px tall and square, so it matches the filter
                 * chips it now sits under.
                 */
                styles={{
                    control: (base, { isFocused }) => ({
                        ...base,
                        cursor: 'pointer',
                        font: 'inherit',
                        minHeight: 33,
                        borderRadius: 0,
                        backgroundColor: 'rgb(var(--ds-surface-000))',
                        borderColor: isFocused ? 'rgb(var(--ds-surface-600))' : 'rgb(var(--ds-surface-300))',
                        '&:hover': {
                            borderColor: isFocused ? 'rgb(var(--ds-surface-600))' : 'rgb(var(--ds-surface-400))'
                        },
                        boxShadow: 'none'
                    }),
                    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
                    singleValue: (base) => ({ ...base, font: 'inherit', color: 'rgb(var(--ds-ink-400))' }),
                    indicatorSeparator: () => ({ display: 'none' }),
                    dropdownIndicator: (base) => ({
                        ...base,
                        padding: 4,
                        color: 'rgb(var(--ds-ink-400))',
                        '&:hover': { color: 'rgb(var(--ds-ink-400))' }
                    }),
                    option: (base, { isFocused, isSelected }) => ({
                        ...base,
                        cursor: 'pointer',
                        font: 'inherit',
                        zIndex: 1,
                        backgroundColor: isSelected
                            ? 'rgb(var(--ds-surface-600))'
                            : isFocused
                                ? 'rgb(var(--ds-surface-200))'
                                : 'transparent',
                        color: isSelected ? 'rgb(var(--ds-ink-100))' : 'rgb(var(--ds-ink-400))',
                        ':active': {
                            backgroundColor: isSelected
                                ? 'rgb(var(--ds-surface-600))'
                                : 'rgb(var(--ds-surface-300))'
                        }
                    }),
                    menuList: (baseStyles) => ({ ...baseStyles, padding: 0 }),
                    menu: (baseStyles) => ({
                        ...baseStyles,
                        zIndex: 100,
                        marginTop: 4,
                        borderRadius: 0,
                        boxShadow: 'none',
                        border: '1px solid rgb(var(--ds-surface-300))',
                        backgroundColor: 'rgb(var(--ds-surface-100))'
                    })
                }}
                defaultValue={itemsPerPage}
                isDisabled={false}
                isLoading={false}
                isClearable={false}
                isSearchable={false}
                name="color"
                options={itemsPerPageOptions}
                onChange={(selected) => {
                    if (selected) {
                        setItemsPerPage(selected);
                        itemsPerPageChanged(selected.value);
                        pageChanged(0);
                    }
                }}
            />
        </div>
    );

    return (
        /*
         * md:items-center, not the default stretch: the three groups are
         * different heights — a bare line of text, 32px page buttons and the
         * 33px Display select — so with stretch the count sat on the top edge
         * of its box while the controls centred inside theirs, and nothing on
         * the row shared a centreline. Only at md, because the column below it
         * wants its children full width.
         */
        <div className="text-body-400 flex flex-col justify-between px-[16px] py-[16px] text-ink-300 md:flex-row md:items-center md:px-0">

            <div className="hidden md:block w-[146px]">{itemsCounter}</div>
            <div className="flex flex-row items-center mx-auto">
                <button
                    onClick={() => pageChanged((currentPage - 2) * data.limit)}
                    disabled={currentPage === 1}
                    aria-label={i18next.t('common:pagination.previous')}
                    className="mr-[8px] flex size-[32px] items-center justify-center text-ink-400 transition-colors hover:enabled:bg-surface-200 disabled:text-surface-400 disabled:!cursor-not-allowed [&_*]:disabled:!cursor-not-allowed"
                >
                    {/* Arrow, not chevron — the DS set (35:6353) draws the page
                        steps with a tail and reserves the bare chevron for
                        disclosure, which is what the Display select uses. The
                        left one was the right-facing glyph under rotate-180. */}
                    <Icon name="arrow-left" size={20} />
                </button>
                <div className="hide-extra-pages">
                    {pagesToShow.map((page) => {
                        if (page === -1) {
                            return <span key={page} className="text-body-400 continue-page inline-block size-[32px] cursor-default text-center text-ink-300">...</span>;
                        }
                        return (
                            <button
                                key={page}
                                onClick={() => pageChanged((page - 1) * data.limit)}
                                aria-current="page"
                                className={clsx(
                                    // Square, not a pill, and the current page
                                    // takes the same dark fill as a primary
                                    // button rather than the old blue.
                                    'text-body-400 numbered-page mx-[8px] size-[32px] rounded-none transition-colors',
                                    page === currentPage
                                        ? 'bg-surface-600 text-ink-100 hover:bg-surface-500'
                                        : 'text-ink-400 hover:bg-surface-200'
                                )}
                            >
                                {page}
                            </button>
                        );
                    })}
                </div>
                <button
                    onClick={() => pageChanged((currentPage) * data.limit)}
                    disabled={currentPage === totalPages}
                    aria-label={i18next.t('common:pagination.next')}
                    className="ml-[8px] flex size-[32px] items-center justify-center text-ink-400 transition-colors hover:enabled:bg-surface-200 disabled:text-surface-400 disabled:!cursor-not-allowed [&_*]:disabled:!cursor-not-allowed"
                >
                    <Icon name="arrow-right" size={20} />
                </button>
            </div>
            <div className="hidden md:block">{itemsPerPageSelctor}</div>
            <div className="md:hidden flex justify-between items-center flex-row mt-[16px]">
                <div>{itemsCounter}</div>
                <div>{itemsPerPageSelctor}</div>
            </div>
        </div>
    );
};

export default Pagination;
